<template>
  <section>
    <!-- Hero -->
    <base-page-heading :title="$t('report.title')">
      <template #extra>
        <b-breadcrumb class="breadcrumb-alt">
          <b-breadcrumb-item>
            <router-link to="/">{{ $t("layout.breadcrumb.home") }}</router-link>
          </b-breadcrumb-item>
          <b-breadcrumb-item active>{{ $t("report.title") }}</b-breadcrumb-item>
        </b-breadcrumb>
      </template>
    </base-page-heading>
    <!-- END Hero -->

    <div class="content">
      <b-row>
        <b-col xl="12">
          <base-block
            :title="$t('report.form.title')"
            ref="filter"
            header-bg
            content-full
            btn-option-content
          >
            <div class="form-row form-group">
              <label class="col-12 col-sm-4">{{
                $t("report.form.priceDate")
              }}</label>
              <div class="col-12 col-sm-8">
                <b-input v-model="report.priceDate" type="date" />
              </div>
            </div>
            <div class="form-row form-group">
              <label for="report-base-competitor" class="col-12 col-sm-4">{{
                $t("report.form.baseCompetitor")
              }}</label>
              <div class="col-12 col-sm-8">
                <select
                  id="report-base-competitor"
                  v-model="report.baseCompetitor"
                  class="form-control"
                >
                  <option
                    v-for="competitor in competitors"
                    v-bind:key="competitor.competitorId"
                    v-bind:value="competitor.competitorId"
                  >
                    {{ competitor.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row form-group">
              <label for="report-comparison-group" class="col-12 col-sm-4">{{
                $t("report.form.comparisonGroup.title")
              }}</label>
              <div class="col-12 col-sm-8">
                <select
                  id="report-comparison-group"
                  v-model="report.comparisonGroup"
                  class="form-control"
                >
                  <option value="0"
                    >{{ $t("report.form.comparisonGroup.allGroups") }}
                  </option>
                  <option
                    v-for="comparisonGroup in comparisonGroups"
                    v-bind:key="comparisonGroup.comparisonGroupId"
                    v-bind:value="comparisonGroup.comparisonGroupId"
                  >
                    {{ comparisonGroup.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="form-row form-group">
              <label class="col-12 col-sm-4">{{
                $t("report.form.keywords")
              }}</label>
              <div class="col-12 col-sm-8">
                <b-form-checkbox-group
                  v-model="report.keywords"
                  switches
                  class="text-left row"
                >
                  <b-form-checkbox
                    v-for="keyword in keywords"
                    v-bind:key="keyword.id"
                    v-bind:value="keyword.id"
                    class="col-5"
                    >{{ keyword.keyword }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </div>
            </div>
            <div class="form-row form-group">
              <label class="col-12 col-sm-4">{{
                $t("report.form.competitors")
              }}</label>
              <div class="col-12 col-sm-8">
                <b-form-checkbox-group
                  v-model="report.competitors"
                  switches
                  class="text-left row"
                >
                  <b-form-checkbox
                    v-for="competitor in competitors"
                    v-bind:key="competitor.competitorId"
                    v-bind:value="competitor.competitorId"
                    class="col-5"
                  >
                    {{ competitor.name }}
                  </b-form-checkbox>
                </b-form-checkbox-group>
              </div>
            </div>
            <div class="form-row form-group">
              <label for="report-comparison" class="col-12 col-sm-4">{{
                $t("report.form.comparison.title")
              }}</label>
              <div class="col-12 col-sm-8">
                <select
                  id="report-comparison"
                  v-model="report.comparison"
                  class="form-control"
                >
                  <option value="week"
                    >{{ $t("report.form.comparison.week") }}
                  </option>
                  <option value="month"
                    >{{ $t("report.form.comparison.month") }}
                  </option>
                  <option value="quarter"
                    >{{ $t("report.form.comparison.quarter") }}
                  </option>
                  <option value="half"
                    >{{ $t("report.form.comparison.half") }}
                  </option>
                  <option value="year"
                    >{{ $t("report.form.comparison.year") }}
                  </option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <button
                  @click="getInitialReport"
                  class="btn btn-primary btn-block"
                >
                  {{ $t("report.form.button") }}
                </button>
              </div>
            </div>
          </base-block>
        </b-col>
      </b-row>
      <b-row v-if="reportData.length > 0">
        <b-col xl="12">
          <base-block
            :title="$t('report.diagram.title')"
            header-bg
            content-full
            btn-option-content
          >
            <chartjs-bar
              v-if="globalComparison.length === 1"
              :chart-data="chartjsData"
              :options="chartjsOptions"
            ></chartjs-bar>
            <chartjs-line
              v-else
              :chart-data="chartjsData"
              :options="chartjsOptions"
            ></chartjs-line>
          </base-block>
        </b-col>
      </b-row>
      <b-row v-if="reportData.length > 0">
        <b-col xl="12">
          <base-block :title="$t('report.report.title')" header-bg content-full>
            <b-table-simple
              responsive
              striped
              hover
              borderless
              class="table-vcenter font-size-sm mb-0"
            >
              <b-thead>
                <b-tr>
                  <b-th class="font-w700">{{ $t("report.report.brand") }}</b-th>
                  <b-th
                    class="d-sm-table-cell font-w700"
                    v-for="(reportHead, index) in reportData"
                    v-bind:key="'td' + index"
                  >
                    {{ $d(new Date(reportHead.priceDate), "short") }}
                  </b-th>
                  <b-th>
                    <b-button
                      size="sm"
                      variant="alt-primary"
                      @click="addTimeStep"
                    >
                      <i class="fa fa-fw fa-plus"></i>
                    </b-button>
                  </b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="competitor in globalComparison[0].report"
                  :key="competitor.competitorId"
                >
                  <b-td
                    :class="{
                      'text-bold':
                        report.baseCompetitor === competitor.competitorId
                    }"
                  >
                    {{ competitor.competitorName }}
                  </b-td>
                  <template v-for="(reportLine, index) in reportData">
                    <template v-for="priceDateLine in reportLine.report">
                      <b-td
                        v-if="
                          priceDateLine.competitorId === competitor.competitorId
                        "
                        class="d-sm-table-cell"
                        v-bind:key="
                          'td' + index + '-' + priceDateLine.competitorId
                        "
                        :class="{
                          'text-green': priceDateLine.relativePrice > 0,
                          'text-red': priceDateLine.relativePrice < 0,
                          'text-bold':
                            report.baseCompetitor === competitor.competitorId
                        }"
                      >
                        {{ $n(priceDateLine.relativePrice, "percent") }}
                      </b-td>
                    </template>
                  </template>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </base-block>
        </b-col>
      </b-row>
      <b-row v-show="usedComparisonGroups.length > 0">
        <b-col xl="12">
          <base-block
            :title="$t('report.comparisonGroups.title')"
            ref="comparisonGroups"
            header-bg
            content-full
            btn-option-content
          >
            <b-table-simple
              responsive
              striped
              hover
              borderless
              class="table-vcenter font-size-sm mb-0"
            >
              <b-thead>
                <b-tr>
                  <b-th></b-th>
                  <b-th
                    >{{ $t("report.comparisonGroups.comparisonGroup") }}
                  </b-th>
                  <b-th></b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr
                  v-for="(group, index) in usedComparisonGroups"
                  :key="'group' + index"
                >
                  <b-td>
                    <img
                      :src="group.refImage"
                      class="group-img"
                      v-if="group.refImage !== ''"
                      alt="item picture"
                    />
                  </b-td>
                  <b-td>{{ group.name }}</b-td>
                  <b-td>
                    <router-link
                      :to="
                        '/admin/comparison_groups/' +
                          group.comparisonGroupId +
                          '/'
                      "
                    >
                      <b-button size="sm" variant="alt-primary">
                        <i class="fa fa-fw fa-angle-right"></i>
                      </b-button>
                    </router-link>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </base-block>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
// @ is an alias to /src

import Loading from "@/store/loading";
import Competitors from "@/assets/js/Competitors";
import ComparisonGroups from "@/assets/js/ComparisonGroups";
import Report from "@/assets/js/Report";
import Notification from "@/assets/js/Notification";
import Keywords from "@/assets/js/Keywords";
import ChartjsLine from "@/components/Chartjs/Line";
import ChartjsBar from "@/components/Chartjs/Bar";
import string2color from "@/assets/js/StringToColor";
import i18n from "@/i18n";

export default {
  name: "Report",
  components: {
    ChartjsLine,
    ChartjsBar
  },
  data() {
    return {
      chartjsOptions: {
        maintainAspectRatio: false,
        scales: {
          yAxes: [
            {
              ticks: {
                callback: function(value) {
                  return i18n.n(value, "percent");
                }
              }
            }
          ]
        },
        tooltips: {
          callbacks: {
            label: function(tooltipItem, data) {
              var label = data.datasets[tooltipItem.datasetIndex].label || "";

              if (label) {
                label += ": ";
              }
              label += i18n.n(tooltipItem.yLabel, "percent");
              return label;
            }
          }
        }
      },
      report: {
        priceDate: new Date().toISOString().slice(0, 10),
        baseCompetitor: 1,
        comparisonGroup: 0,
        keywords: [],
        competitors: [],
        comparison: "week",
        timeSteps: 1
      },
      reportData: [],
      competitors: [],
      comparisonGroups: [],
      keywords: [],
      globalComparison: [],
      usedComparisonGroups: [],
      lastDate: null
    };
  },
  mounted() {
    Notification.init(this);

    document.title = this.$t("report.title");

    Loading.dispatch("increase", 3);

    Competitors.get().then(competitors => {
      if (this.report.competitors.length === 0) {
        for (let i in competitors) {
          if (Object.prototype.hasOwnProperty.call(competitors, i)) {
            this.report.competitors.push(competitors[i].competitorId);
          }
        }
      }

      this.$set(this, "competitors", competitors);
      Loading.dispatch("reduce");
    });

    Keywords.getList().then(keywordList => {
      this.keywords = keywordList;
      Loading.dispatch("reduce");
    });

    ComparisonGroups.get().then(groupList => {
      this.comparisonGroups = groupList;
      Loading.dispatch("reduce");
    });

    this.readQuery();
  },
  methods: {
    readQuery() {
      if (
        typeof this.$route.query.date === "string" ||
        typeof this.$route.query.group === "string"
      ) {
        if (typeof this.$route.query.date === "string") {
          this.report.priceDate = new Date(this.$route.query.date)
            .toISOString()
            .slice(0, 10);
        }

        if (typeof this.$route.query.base === "string") {
          this.report.baseCompetitor = this.$route.query.base;
        }

        if (typeof this.$route.query.group === "string") {
          this.report.comparisonGroup = parseInt(this.$route.query.group);
          this.report.comparisonGroup =
            this.report.comparisonGroup < 1 ? 0 : this.report.comparisonGroup;
        }

        if (typeof this.$route.query.time === "string") {
          this.report.comparison = this.$route.query.time;
        }

        if (typeof this.$route.query.steps === "string") {
          this.report.timeSteps = parseInt(this.$route.query.steps);
          this.report.timeSteps =
            this.report.timeSteps < 1 ? 1 : this.report.timeSteps;
        }

        if (
          typeof this.$route.query.keywords === "string" &&
          this.$route.query.keywords !== ""
        ) {
          this.report.keywords = this.$route.query.keywords.split(",");
        }

        if (
          typeof this.$route.query.competitors === "string" &&
          this.$route.query.competitors !== ""
        ) {
          this.report.competitors = [];
          this.report.competitors = this.$route.query.competitors.split(",");
        }

        this.getInitialReport();
      }
    },
    buildQuery() {
      let queryPara = {
        date: this.report.priceDate,
        base: this.report.baseCompetitor,
        keywords: this.report.keywords.join(","),
        competitors: this.report.competitors.join(","),
        group: this.report.comparisonGroup,
        time: this.report.comparison,
        steps: this.reportData.length < 1 ? 1 : this.reportData.length
      };

      let params = Object.keys(queryPara)
        .map(key => key + "=" + queryPara[key])
        .join("&");
      history.pushState({}, null, this.$route.path + "?" + params);
    },
    addTimeStep() {
      let lastWeekPriceDate = new Date(this.lastDate);

      switch (this.report.comparison) {
        case "month":
          lastWeekPriceDate.setMonth(lastWeekPriceDate.getMonth() - 1);
          break;
        case "quarter":
          lastWeekPriceDate.setMonth(lastWeekPriceDate.getMonth() - 3);
          break;
        case "half":
          lastWeekPriceDate.setMonth(lastWeekPriceDate.getMonth() - 6);
          break;
        case "year":
          lastWeekPriceDate.setFullYear(lastWeekPriceDate.getFullYear() - 1);
          break;
        default:
          lastWeekPriceDate.setDate(lastWeekPriceDate.getDate() - 7);
      }

      this.getReport(lastWeekPriceDate.toDateString());
    },
    getInitialReport() {
      this.$set(this, "usedComparisonGroups", []);
      this.$set(this, "globalComparison", []);
      this.getReport();
    },
    getReport(priceDate) {
      this.$refs.filter.contentHide();

      Loading.dispatch("increase", 1);

      let reportPriceDate =
        typeof priceDate !== "undefined" ? priceDate : this.report.priceDate;

      Report.get(
        reportPriceDate,
        this.report.baseCompetitor,
        this.report.keywords,
        this.report.competitors,
        this.report.comparisonGroup
      ).then(report => {
        if (report.report.length === 0) {
          Notification.push(
            this.$t("report.empty.title"),
            this.$t("report.empty.text", {
              date: this.$d(new Date(report.priceDate), "short")
            }),
            "danger"
          );

          if (this.globalComparison.length === 0) {
            this.$refs.filter.contentShow();
          }
        } else {
          let reportList = this.globalComparison;
          reportList.push(report);

          this.$set(this, "globalComparison", reportList);

          this.lastDate = report.priceDate;

          if (
            this.usedComparisonGroups.length === 0 &&
            report.comparisonGroups.length > 0
          ) {
            this.usedComparisonGroups = report.comparisonGroups;
            this.$refs.comparisonGroups.contentHide();
          }
        }

        this.reportData = JSON.parse(
          JSON.stringify(this.globalComparison)
        ).reverse();

        this.buildQuery();

        if (
          this.globalComparison.length !== 0 &&
          this.globalComparison.length < this.report.timeSteps
        ) {
          this.addTimeStep();
        }

        Loading.dispatch("reduce");
      });
    }
  },
  watch: {
    "report.comparisonGroup": {
      handler: function() {
        if (this.report.comparisonGroup > 0) {
          this.report.keywords = [];
        }
      },
      deep: true
    },
    "report.keywords": {
      handler: function() {
        if (this.report.keywords.length > 0) {
          this.report.comparisonGroup = 0;
        }
      },
      deep: true
    }
  },
  computed: {
    chartjsData: function() {
      let chartjsData = {
        labels: [],
        datasets: []
      };

      let data = [];

      for (let priceDateIndex in this.reportData) {
        if (
          Object.prototype.hasOwnProperty.call(this.reportData, priceDateIndex)
        ) {
          let date = this.$d(
            new Date(this.reportData[priceDateIndex].priceDate),
            "short"
          );
          chartjsData.labels.push(date);

          for (let lineIndex in this.reportData[priceDateIndex].report) {
            if (
              Object.prototype.hasOwnProperty.call(
                this.reportData[priceDateIndex].report,
                lineIndex
              )
            ) {
              let competitorId = this.reportData[priceDateIndex].report[
                lineIndex
              ].competitorId;
              if (typeof data[competitorId] !== "object") {
                let competitorColor = string2color(
                  this.reportData[priceDateIndex].report[lineIndex]
                    .competitorName
                );
                data[competitorId] = {
                  label: this.reportData[priceDateIndex].report[lineIndex]
                    .competitorName,
                  fill: false,
                  borderColor: competitorColor,
                  backgroundColor: competitorColor,
                  data: []
                };
              }

              data[competitorId].data.push(
                this.reportData[priceDateIndex].report[lineIndex].relativePrice
              );
            }
          }
        }
      }

      for (let lineIndex in this.reportData[0].report) {
        if (
          Object.prototype.hasOwnProperty.call(
            this.reportData[0].report,
            lineIndex
          )
        ) {
          let competitorId = this.reportData[0].report[lineIndex].competitorId;
          if (Object.prototype.hasOwnProperty.call(data, competitorId)) {
            chartjsData.datasets.push(data[competitorId]);
          }
        }
      }

      return chartjsData;
    }
  }
};
</script>

<style lang="scss" scoped="scoped">
.group-img {
  width: 10em;
  height: auto;
  border: solid 0.05em #2c3e50;
}
</style>
